<template>
    <div>
        <div
            v-if="!file"
            :class="[
                'flex flex-col items-center justify-center rounded-lg border-2 border-dashed bg-gray-50 p-10 text-center text-gray-400 cursor-pointer',
                hover ? '!border-blue-600' : 'border-gray-300'
            ]"
            @click="$refs.fileInput.click()"
            @dragover.prevent
            @drop.prevent="handleDrop"
            @dragenter.prevent="hover = true"
            @dragleave.prevent="hover = false"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"/><path d="M7 9l5 -5l5 5"/><path d="M12 4l0 12"/></svg>
            <div class="mt-4">
                <label for="csv-file" class="cursor-pointer" @click.stop>
                    <span class="text-sm font-semibold text-blue-600 hover:text-blue-500">Upload a file</span>
                    <input ref="fileInput" id="csv-file" type="file" accept=".csv" @change="handleFile" class="sr-only" />
                </label>
                <span class="text-sm text-gray-600"> or drag and drop</span>
            </div>
            <p class="mt-2 mb-0 text-xs text-gray-500">CSV files up to {{ maxSizeKB }}KB</p>
        </div>

        <div v-else class="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div class="flex items-center space-x-3">
                <div class="rounded-lg bg-blue-100 p-2 text-blue-600">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/><path d="M9 15l2 2l4 -4"/></svg>
                </div>
                <div>
                    <p class="mb-0 text-sm font-medium text-gray-900">{{ file.name }}</p>
                    <p class="mb-0 text-xs text-gray-500">{{ formatSize(file.size) }}</p>
                </div>
            </div>
            <button @click="removeFile" class="text-gray-400 hover:text-gray-600">
                <span class="lnr icon-cross !text-lg"></span>
            </button>
        </div>

        <div v-if="error" class="mt-4 flex items-center gap-2 rounded-md bg-red-50 p-3 text-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg>
            <p class="pb-0 mb-0 text-sm text-red-800">{{ error }}</p>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
    maxSizeKB: {
        type: Number,
        default: 10240
    }
});

const emit = defineEmits(['file-selected', 'file-removed', 'error']);

const file = ref(null);
const error = ref('');
const hover = ref(false);

const handleFile = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile || !selectedFile.name.endsWith('.csv')) {
        error.value = 'Please upload a valid CSV file';
        emit('error', error.value);
        return;
    }

    if (selectedFile.size > props.maxSizeKB * 1024) {
        error.value = `File size exceeds ${props.maxSizeKB}KB limit`;
        emit('error', error.value);
        return;
    }

    file.value = selectedFile;
    error.value = '';
    emit('file-selected', selectedFile);
};

const handleDrop = (e) => {
    hover.value = false;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
        handleFile({ target: { files } });
    }
};

const removeFile = () => {
    file.value = null;
    error.value = '';
    emit('file-removed');
};

const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);
    if (i === 0) {
        return `${bytes} Bytes`;
    } else if (i === 1) {
        return `${Math.round(size * 100) / 100} KB`;
    } else {
        return `${Math.round(size * 100) / 100} MB`;
    }
};

defineExpose({
    removeFile
});
</script>